import { CameraCapturedPicture } from "expo-camera";
import { ImagePickerAsset } from "expo-image-picker";
import { Platform, useWindowDimensions } from "react-native";
import { commonCameraRatios } from "./constants";
import { useMemo } from "react";
import { manipulateAsync } from "expo-image-manipulator";
import { Image } from "react-native";

export const getBase64 = (asset: CameraCapturedPicture | ImagePickerAsset) =>
  Platform.OS === "web" ? asset.base64 || asset.uri : asset.base64;

export const ratioStringToNumber = (ratio: string) => {
  const [width, height] = ratio.split(":");
  return Number(height) / Number(width);
};

// set the camera ratio and padding.
// this code assumes a portrait mode screen
export const usePrepareRatio = () => {
  const { width, height } = useWindowDimensions();

  const prepareRatio = (width: number, height: number) => {
    const screenRatio = height / width;

    let desiredRatio = "4:3"; // Start with the system default
    let remainder = 0;

    // This issue only affects web and android
    if (Platform.OS === "web" || Platform.OS === "android") {
      const ratios = commonCameraRatios;

      // Calculate the width/height of each of the supported camera ratios
      // These width/height are measured in landscape mode
      // find the ratio that is closest to the screen ratio without going over
      let distances: Record<string, number> = {};
      let realRatios: Record<string, number> = {};
      let minDistance = null;

      for (const ratio of ratios) {
        const realRatio = ratioStringToNumber(ratio);
        realRatios[ratio] = realRatio;
        // ratio can't be taller than screen, so we don't want an abs()
        const distance = screenRatio - realRatio;
        distances[ratio] = realRatio;
        if (minDistance == null) {
          minDistance = ratio;
        } else {
          if (distance >= 0 && distance < distances[minDistance]) {
            minDistance = ratio;
          }
        }
      }

      // set the best match
      if (minDistance) {
        desiredRatio = minDistance;
      }
      // //  calculate the difference between the camera width and the screen height
      // console.log({
      //   height,
      //   realRatios,
      //   selected: realRatios[desiredRatio],
      //   width,
      // });
      remainder = Math.floor((height - realRatios[desiredRatio] * width) / 2);
    }

    return {
      imagePadding: remainder,
      ratio: desiredRatio,
    };
  };

  const preparedRatio = useMemo(
    () => prepareRatio(width, height),
    [width, height]
  );

  return preparedRatio;
};

// Function to handle image manipulation
export const cropToAspectRatio = async (
  photo: CameraCapturedPicture,
  aspectRatio: number
) => {
  try {
    const manipResult = await manipulateAsync(
      getBase64(photo)!,
      [{ crop: getCropParams(photo, aspectRatio) }],
      { base64: true }
    );

    return manipResult;
  } catch {
    return undefined;
  }
};

// Calculate crop parameters based on the aspect ratio
const getCropParams = (photo: CameraCapturedPicture, aspectRatio: number) => {
  const base64 = getBase64(photo);

  if (!base64) throw new Error("No base64 found");

  // Get image dimensions using Expo's Asset API
  const { width, height } = photo; //await getDimensionsFromBase64(base64);

  // Calculate crop dimensions to match the desired aspect ratio
  let cropWidth = width;
  let cropHeight = width / aspectRatio;

  if (cropHeight > height) {
    cropHeight = height;
    cropWidth = height * aspectRatio;
  }

  const xOffset = (width - cropWidth) / 2;
  const yOffset = (height - cropHeight) / 2;

  return {
    originX: xOffset,
    originY: yOffset,
    width: cropWidth,
    height: cropHeight,
  };
};

type Dimensions = { width: number; height: number };

const getDimensionsFromBase64 = async (base64: string): Promise<Dimensions> => {
  return new Promise<Dimensions>((resolve, reject) => {
    Image.getSize(
      base64,
      (width, height) => resolve({ width, height }),
      (error) => reject({ error })
    );
  });
};
